/**
 * @file Shared currency text-input behaviour.
 */

export const CURRENCY_INPUT_ALLOWED_PATTERN = /^[0-9.,$\-\s]*$/;

/**
 * Removes characters that are not useful in currency entry.
 * @param {unknown} value Raw input value.
 * @returns {string} Sanitized currency text.
 */
export function sanitizeCurrencyInputValue(value) {
  return String(value || "").replace(/[^0-9.,$\-\s]/g, "");
}

/**
 * Restricts a currency text input to numeric and currency punctuation.
 * @param {HTMLInputElement} input Currency input.
 * @param {object} [options] Optional callbacks.
 * @param {Function} [options.onInput] Called after cleaned input updates.
 * @returns {void}
 */
export function restrictCurrencyInput(input, options = {}) {
  if (input.dataset.currencyRestrictionBound === "true") {return;}
  input.dataset.currencyRestrictionBound = "true";

  input.addEventListener("beforeinput", (event) => {
    if (!event.data ||
        event.inputType?.startsWith("delete") ||
        event.inputType?.startsWith("history")) {
      return;
    }
    if (event.inputType === "insertFromPaste") {return;}
    if (!CURRENCY_INPUT_ALLOWED_PATTERN.test(event.data)) {
      event.preventDefault();
    }
  });

  input.addEventListener("input", () => {
    const originalValue = input.value;
    const cleanedValue = sanitizeCurrencyInputValue(originalValue);
    if (cleanedValue === originalValue) {
      options.onInput?.();
      return;
    }

    const selectionStart = input.selectionStart ?? originalValue.length;
    const cleanedBeforeCursor = sanitizeCurrencyInputValue(
      originalValue.slice(0, selectionStart),
    );
    input.value = cleanedValue;
    input.setSelectionRange?.(cleanedBeforeCursor.length, cleanedBeforeCursor.length);
    options.onInput?.();
  });
}
